import React, { useEffect, useState } from 'react';
import { useApp } from '../App';

const STATUS_LABELS = {
  open: 'Open',
  accepted: 'Accepted',
  implemented: 'Implemented',
  dismissed: 'Dismissed',
};

const CATEGORY_LABELS = {
  idle: 'Idle Resource',
  downsize: 'Rightsizing',
  upsize: 'Upsize',
  pricing: 'Pricing',
  reservation: 'Reservation Gap',
};

function fmt(usd) {
  if (usd === 0) return '—';
  return `$${Math.abs(usd).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/mo`;
}

function timeAgo(input) {
  if (!input) return '—';
  const t = new Date(input).getTime();
  if (Number.isNaN(t)) return '—';
  const diffMs = Date.now() - t;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export default function RecommendationsPage() {
  const { currentUser, recommendations, tenantUsers, cloudAccountRoleById, handleAssignRecommendation, handleUpdateRecommendationStatus } =
    useApp();

  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('savings_desc');
  const [assignBusy, setAssignBusy] = useState({});
  const [statusBusy, setStatusBusy] = useState({});
  const [pageError, setPageError] = useState('');
  const [pageMessage, setPageMessage] = useState('');

  useEffect(() => {
    setPageError('');
    setPageMessage('');
  }, [statusFilter]);

  const filtered = (recommendations || [])
    .filter((r) => (statusFilter ? r.status === statusFilter : true))
    .filter((r) => (categoryFilter ? r.category === categoryFilter : true))
    .filter((r) => {
      const q = searchQuery.trim().toLowerCase();
      if (!q) return true;
      return [r.title, r.rationale, r.assigned_to_email, r.category]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    })
    .sort((a, b) => {
      if (sortBy === 'savings_desc') return (b.estimated_monthly_delta_usd || 0) - (a.estimated_monthly_delta_usd || 0);
      if (sortBy === 'risk_desc') return (b.risk_score || 0) - (a.risk_score || 0);
      if (sortBy === 'confidence_desc') return (b.confidence_score || 0) - (a.confidence_score || 0);
      return String(a.title || '').localeCompare(String(b.title || ''));
    });

  async function onAssign(rec, userId) {
    setAssignBusy((prev) => ({ ...prev, [rec.id]: true }));
    setPageError('');
    setPageMessage('');
    try {
      await handleAssignRecommendation(rec.id, userId || null);
      setPageMessage('Recommendation assigned.');
    } catch (err) {
      setPageError(err.message || 'Assignment failed.');
    } finally {
      setAssignBusy((prev) => {
        const next = { ...prev };
        delete next[rec.id];
        return next;
      });
    }
  }

  async function onStatusChange(rec, newStatus) {
    setStatusBusy((prev) => ({ ...prev, [rec.id]: true }));
    setPageError('');
    setPageMessage('');
    try {
      await handleUpdateRecommendationStatus(rec.id, newStatus);
      setPageMessage('Status updated.');
    } catch (err) {
      setPageError(err.message || 'Status update failed.');
    } finally {
      setStatusBusy((prev) => {
        const next = { ...prev };
        delete next[rec.id];
        return next;
      });
    }
  }

  const totalSavings = filtered.reduce(
    (sum, r) => sum + (r.estimated_monthly_delta_usd || 0),
    0
  );

  const byStatus = {
    open: filtered.filter((r) => r.status === 'open').length,
    accepted: filtered.filter((r) => r.status === 'accepted').length,
    implemented: filtered.filter((r) => r.status === 'implemented').length,
  };

  function exportFilteredCsv() {
    const rows = [
      ['Resource', 'Issue', 'Risk', 'Potential Savings (USD/mo)', 'Status', 'Owner', 'Last Updated'],
      ...filtered.map((rec) => [
        rec.resource_id || rec.id,
        rec.title || '',
        riskLabel(Number(rec.risk_score || 0)),
        Number(rec.estimated_monthly_delta_usd || 0).toFixed(2),
        rec.status || '',
        rec.assigned_to_email || '',
        rec.updated_at || rec.created_at || '',
      ]),
    ];
    const csv = rows
      .map((row) => row.map((cell) => `"${String(cell).replaceAll('"', '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sroik-recommendations.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  function riskLabel(score) {
    if (score >= 70) return 'High';
    if (score >= 40) return 'Medium';
    return 'Low';
  }

  function riskClass(score) {
    if (score >= 70) return 'risk-badge risk-badge-high';
    if (score >= 40) return 'risk-badge risk-badge-med';
    return 'risk-badge risk-badge-low';
  }

  return (
    <div id="actions" className="page-view recommendations-view">
      <div className="page-header">
        <p className="feature-eyebrow">Optimization Queue</p>
        <h2>Recommendations</h2>
        <p className="page-subtitle">Prioritize high-impact items, assign owners, and move decisions to delivery.</p>
        {pageMessage && <p className="auth-feedback auth-success">{pageMessage}</p>}
        {pageError && <p className="auth-feedback auth-error">{pageError}</p>}
      </div>

      <div className="workspace-block recommendation-status-tabs">
        <button type="button" className={statusFilter === '' ? 'status-tab active' : 'status-tab'} onClick={() => setStatusFilter('')}>
          All ({filtered.length})
        </button>
        <button type="button" className={statusFilter === 'open' ? 'status-tab active' : 'status-tab'} onClick={() => setStatusFilter('open')}>
          Open ({byStatus.open})
        </button>
        <button type="button" className={statusFilter === 'accepted' ? 'status-tab active' : 'status-tab'} onClick={() => setStatusFilter('accepted')}>
          Accepted ({byStatus.accepted})
        </button>
        <button type="button" className={statusFilter === 'implemented' ? 'status-tab active' : 'status-tab'} onClick={() => setStatusFilter('implemented')}>
          Implemented ({byStatus.implemented})
        </button>
      </div>

      <div className="workspace-block recommendation-toolbar">
        <label>
          Search
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search recommendations"
          />
        </label>
        <label>
          Category
          <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
            <option value="">All</option>
            {Object.entries(CATEGORY_LABELS).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
        </label>
        <label>
          Sort By
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="savings_desc">Savings (highest first)</option>
            <option value="risk_desc">Risk score (highest first)</option>
            <option value="confidence_desc">Confidence (highest first)</option>
            <option value="title_asc">Title (A-Z)</option>
          </select>
        </label>
        <div className="recommendation-toolbar-actions">
          <button type="button" className="secondary-action" onClick={exportFilteredCsv}>Export CSV</button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <p className="empty-state">No recommendations found.</p>
      ) : (
        <div className="workspace-block recommendation-table-wrap">
          <table className="account-breakdown-table recommendation-table">
            <thead>
              <tr>
                <th>Resource</th>
                <th>Issue</th>
                <th>Risk</th>
                <th style={{ textAlign: 'right' }}>Potential Savings</th>
                <th>Status</th>
                <th>Last Updated</th>
                <th>Owner</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((rec) => {
                const accountRole = cloudAccountRoleById?.[rec.cloud_account_id] || currentUser?.role || 'viewer';
                const canAct = ['owner', 'admin', 'analyst', 'superadmin'].includes(accountRole);
                return (
                  <tr key={rec.id}>
                    <td className="account-id-cell">{rec.resource_id || rec.id}</td>
                    <td>
                      <div className="recommendation-row-title">{rec.title}</div>
                      <div className="recommendation-row-sub">{CATEGORY_LABELS[rec.category] || rec.category}</div>
                    </td>
                    <td><span className={riskClass(rec.risk_score)}>{riskLabel(rec.risk_score)}</span></td>
                    <td style={{ textAlign: 'right' }}>{fmt(rec.estimated_monthly_delta_usd)}</td>
                    <td>
                      <select
                        value={rec.status}
                        className="inline-select"
                        disabled={!!statusBusy[rec.id] || !canAct}
                        onChange={(e) => onStatusChange(rec, e.target.value)}
                      >
                        {Object.entries(STATUS_LABELS).map(([v, l]) => (
                          <option key={v} value={v}>{l}</option>
                        ))}
                      </select>
                    </td>
                    <td>{timeAgo(rec.updated_at || rec.created_at)}</td>
                    <td>
                      <select
                        value={rec.assigned_to_user_id || ''}
                        className="inline-select"
                        disabled={!!assignBusy[rec.id] || !canAct}
                        onChange={(e) => onAssign(rec, e.target.value)}
                      >
                        <option value="">Unassigned</option>
                        {(tenantUsers || []).map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.full_name ? `${u.full_name} (${u.email})` : u.email}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="recommendation-summary-grid">
        <div className="workspace-block">
          <p className="feature-eyebrow">Items In Scope</p>
          <p className="recommendation-summary-value">{filtered.length}</p>
          <p className="page-subtitle">After filters and search</p>
        </div>
        <div className="workspace-block">
          <p className="feature-eyebrow">Estimated Monthly Savings</p>
          <p className="recommendation-summary-value">{fmt(totalSavings)}</p>
          <p className="page-subtitle">Potential impact represented</p>
        </div>
        <div className="workspace-block">
          <p className="feature-eyebrow">Status Mix</p>
          <p className="page-subtitle">Open {byStatus.open} · Accepted {byStatus.accepted} · Implemented {byStatus.implemented}</p>
        </div>
      </div>
    </div>
  );
}
